import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FaInstagram, FaFacebook, FaLinkedin, FaXTwitter, FaTiktok,
} from "react-icons/fa6";
// IntegrationsCard is temporarily hidden from UI (functionality preserved)
// import IntegrationsCard from "../components/IntegrationsCard";

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isTeamLeader, isSuperAdmin } = useAuth();
  const toast = useToast();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingReports, setSyncingReports] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [form, setForm] = useState({});
  const [profileText, setProfileText] = useState("");
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [editingReportsUrl, setEditingReportsUrl] = useState(false);
  const [reportsUrl, setReportsUrl] = useState("");
  const [editingDriveUrl, setEditingDriveUrl] = useState(false);
  const [driveUrl, setDriveUrl] = useState("");

  useEffect(() => {
    API.get(`/api/clients/${id}`)
      .then(r => {
        setClient(r.data);
        setForm(r.data);
        setProfileText(r.data.strategic_profile || "");
        setReportsUrl(r.data.reports_drive_folder_url || "");
        setDriveUrl(r.data.drive_folder_url || "");
      })
      .catch(() => navigate("/clients"))
      .finally(() => setLoading(false));
  }, [id]);

  const doSync = async () => {
    setShowSyncConfirm(false);
    if (!client.drive_folder_url) return toast("No hay carpeta de Drive configurada", "error");
    setSyncing(true);
    try {
      const r = await API.post(`/api/clients/${id}/sync`);
      setClient(prev => ({ ...prev, strategic_profile: r.data.strategic_profile, strategic_profile_updated_at: new Date() }));
      setProfileText(r.data.strategic_profile);
      toast(`Sincronizado — ${r.data.files_processed} archivos procesados`, "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al sincronizar", "error");
    } finally { setSyncing(false); }
  };

  const handleSync = () => {
    if (client.strategic_profile) setShowSyncConfirm(true);
    else doSync();
  };

  const handleSyncReports = async () => {
    if (!client.reports_drive_folder_url) return toast("No hay carpeta de reportes configurada", "error");
    setSyncingReports(true);
    try {
      const r = await API.post(`/api/clients/${id}/reports/sync`);
      setClient(prev => ({ ...prev, reports_last_synced_at: new Date() }));
      toast(`Reportes sincronizados — ${r.data.files_processed} archivos`, "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al sincronizar reportes", "error");
    } finally { setSyncingReports(false); }
  };

  const handleSaveReportsUrl = async () => {
    try {
      await API.patch(`/api/clients/${id}/reports/folder`, { reports_drive_folder_url: reportsUrl });
      setClient(prev => ({ ...prev, reports_drive_folder_url: reportsUrl }));
      setEditingReportsUrl(false);
      toast("Carpeta de reportes guardada", "success");
    } catch { toast("Error al guardar", "error"); }
  };

  const handleSaveDriveUrl = async () => {
    try {
      await API.patch(`/api/clients/${id}`, { drive_folder_url: driveUrl });
      setClient(prev => ({ ...prev, drive_folder_url: driveUrl }));
      setEditingDriveUrl(false);
      toast("Carpeta de documentos guardada", "success");
    } catch { toast("Error al guardar", "error"); }
  };

  const handleSave = async () => {
    try {
      const r = await API.patch(`/api/clients/${id}`, form);
      setClient(r.data); setEditing(false);
      toast("Cliente actualizado", "success");
    } catch { toast("Error al guardar", "error"); }
  };

  const handleSaveProfile = async () => {
    try {
      const r = await API.patch(`/api/clients/${id}`, { strategic_profile: profileText });
      setClient(r.data); setEditingProfile(false);
      toast("Perfil estratégico actualizado", "success");
    } catch { toast("Error al guardar", "error"); }
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}><div className="spinner" /></div>;
  if (!client) return null;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: "var(--radius-lg)",
              background: client.primary_color || "#0b00f9",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0,
            }}>
              {client.name[0].toUpperCase()}
            </div>
            <div>
              <h1 className="page-title" style={{ marginBottom: 2 }}>{client.name}</h1>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className="page-subtitle">{client.industry || "Sin rubro"}</span>
                <span className={`badge ${client.status === "active" ? "badge-green" : "badge-gray"}`}>
                  {client.status === "active" ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
          </div>
          {isTeamLeader && (
            <div className="page-actions">
              <button className="btn btn-secondary" onClick={() => setEditing(true)}>Editar</button>
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* 3-column grid: Info | Drive & Sync | Integrations */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 24 }}>

          {/* Col 1: Info */}
          <div className="card">
            <h3 style={sectionTitle}>Información</h3>
            <InfoRow label="Nombre" value={client.name} />
            <InfoRow label="Rubro" value={client.industry || "—"} />
            <InfoRow label="Estado" value={client.status === "active" ? "Activo" : "Inactivo"} />
            <InfoRow label="Color" value={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 14, height: 14, borderRadius: 3, background: client.primary_color || "#0b00f9" }} />
                <span>{client.primary_color || "#0b00f9"}</span>
              </div>
            } />
            {isSuperAdmin && (
              <InfoRow
                label="Límite mensual"
                value={
                  client.monthly_limit_usd
                    ? <span style={{ color: "var(--warning)", fontWeight: 500 }}>USD ${parseFloat(client.monthly_limit_usd).toFixed(2)}</span>
                    : <span style={{ color: "var(--text-muted)" }}>Sin límite</span>
                }
              />
            )}
          </div>

          {/* Col 2: Drive & Sync — two sections */}
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* General Drive */}
            <div>
              <h3 style={sectionTitle}>Documentos del Cliente</h3>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.6 }}>
                Brief, documentación técnica, alcance, estrategia, comentarios del cliente, manual de marca, design system, y todos los documentos relacionados al cliente para tener en consideración.
                {" "}Los documentos deben ser guardados en{" "}
                <a
                  href="https://drive.google.com/drive/u/0/folders/1R1c5rqeFHNfLyAh5OAAieTZWkKh4HhXi"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--accent)", textDecoration: "underline" }}
                >
                  este Drive
                </a>
                {" "}/ nombre-del-cliente / smith
              </p>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
                Al sincronizar, todos los agentes consultan directamente el contenido de estos documentos, además de usarlos para generar el Perfil Estratégico.
              </p>

              {editingDriveUrl ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    className="input"
                    value={driveUrl}
                    onChange={e => setDriveUrl(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    style={{ fontSize: 13 }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveDriveUrl}>Guardar</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingDriveUrl(false); setDriveUrl(client.drive_folder_url || ""); }}>Cancelar</button>
                  </div>
                </div>
              ) : client.drive_folder_url ? (
                <>
                  <a href={client.drive_folder_url} target="_blank" rel="noreferrer"
                    style={{ color: "var(--accent)", fontSize: 13, display: "block", marginBottom: 8 }}>
                    Ver carpeta →
                  </a>
                  {client.strategic_profile_updated_at && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                      Último sync: {new Date(client.strategic_profile_updated_at).toLocaleString("es-AR")}
                    </div>
                  )}
                  {isTeamLeader && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
                        {syncing ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Sincronizando...</> : "🔄 Sincronizar"}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingDriveUrl(true)}>Cambiar carpeta</button>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>Sin carpeta configurada.</p>
                  {isTeamLeader && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingDriveUrl(true)}>Configurar carpeta</button>
                  )}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* Reports Drive */}
            <div>
              <h3 style={sectionTitle}>Reportes del Cliente</h3>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
                Los agentes consultan esta carpeta automáticamente para análisis y reportes.
              </p>

              {editingReportsUrl ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input
                    className="input"
                    value={reportsUrl}
                    onChange={e => setReportsUrl(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    style={{ fontSize: 13 }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveReportsUrl}>Guardar</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingReportsUrl(false); setReportsUrl(client.reports_drive_folder_url || ""); }}>Cancelar</button>
                  </div>
                </div>
              ) : client.reports_drive_folder_url ? (
                <>
                  <a href={client.reports_drive_folder_url} target="_blank" rel="noreferrer"
                    style={{ color: "var(--accent)", fontSize: 13, display: "block", marginBottom: 8 }}>
                    Ver carpeta de reportes →
                  </a>
                  {client.reports_last_synced_at && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                      Último sync: {new Date(client.reports_last_synced_at).toLocaleString("es-AR")}
                      <span style={{ marginLeft: 8, color: "var(--success)" }}>· Auto-sync cada 6 días</span>
                    </div>
                  )}
                  {isTeamLeader && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={handleSyncReports} disabled={syncingReports}>
                        {syncingReports ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Sincronizando...</> : "🔄 Sincronizar ahora"}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingReportsUrl(true)}>Cambiar carpeta</button>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>Sin carpeta configurada.</p>
                  {isTeamLeader && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingReportsUrl(true)}>Configurar carpeta</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Col 3: Team assignment */}
          <ClientUsersCard clientId={id} canEdit={isTeamLeader} />
        </div>

        {/* Social accounts */}
        <SocialAccountsCard clientId={id} canEdit={isTeamLeader} />

        {/* Contacts */}
        <ContactsCard clientId={id} canEdit={isTeamLeader} defaultLeaderEmail={client.project_leader_email} defaultPmEmail={client.project_manager_email} />

        {/* PM Email History */}
        <EmailLogCard clientId={id} canEdit={isTeamLeader} />

        {/* Strategic Profile */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={sectionTitle}>Perfil Estratégico</h3>
            {isTeamLeader && client.strategic_profile && !editingProfile && (
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingProfile(true)}>Editar</button>
            )}
          </div>

          {!client.strategic_profile && !syncing && !editingProfile ? (
            <div className="empty-state" style={{ padding: "32px 0" }}>
              <h3>Sin perfil estratégico</h3>
              <p style={{ marginTop: 8, marginBottom: 20 }}>
                {client.drive_folder_url
                  ? "Sincronizá la carpeta de Drive para generarlo, o escribilo manualmente."
                  : "Podés sincronizar desde Drive o escribir el perfil manualmente."}
              </p>
              {isTeamLeader && (
                <button className="btn btn-primary btn-sm" onClick={() => setEditingProfile(true)}>
                  + Agregar manualmente
                </button>
              )}
            </div>
          ) : syncing ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div className="spinner" style={{ width: 28, height: 28, margin: "0 auto 12px" }} />
              <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>Claude está analizando los documentos...</p>
            </div>
          ) : editingProfile ? (
            <div>
              <textarea
                className="textarea"
                value={profileText}
                onChange={e => setProfileText(e.target.value)}
                placeholder={`Escribí el perfil estratégico del cliente. Por ejemplo:\n\n— Nombre del proyecto / producto\n— Objetivo principal\n— Stack tecnológico (si aplica)\n— Funcionalidades clave\n— Audiencia objetivo\n— Notas del contrato o acuerdo\n— Cualquier contexto que los agentes necesiten conocer`}
                style={{ minHeight: 400, fontFamily: "monospace", fontSize: 13, lineHeight: 1.6 }}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button className="btn btn-primary" onClick={handleSaveProfile}>Guardar perfil</button>
                <button className="btn btn-secondary" onClick={() => { setEditingProfile(false); setProfileText(client.strategic_profile || ""); }}>Cancelar</button>
              </div>
            </div>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              className="md-content"
              components={{
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                     style={{ color: "var(--accent)", textDecoration: "underline" }}>
                    {children}
                  </a>
                )
              }}
            >
              {client.strategic_profile}
            </ReactMarkdown>
          )}
        </div>
      </div>

      {/* Sync confirm modal */}
      {showSyncConfirm && (
        <Modal onClose={() => setShowSyncConfirm(false)}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>¿Actualizar perfil estratégico?</h2>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 24, lineHeight: 1.6 }}>
            Ya existe un perfil estratégico. ¿Querés que Claude lo actualice con los nuevos documentos o preferís mantener el actual?
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setShowSyncConfirm(false)}>Mantener actual</button>
            <button className="btn btn-primary" onClick={doSync}>Actualizar perfil</button>
          </div>
        </Modal>
      )}

      {editing && (
        <EditClientModal
          form={form} setForm={setForm}
          onClose={() => { setEditing(false); setForm(client); }}
          onSave={handleSave}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </div>
  );
}

const sectionTitle = {
  fontSize: 12, fontWeight: 600, color: "var(--text-muted)",
  marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.5px",
};

// ── Social platform config ───────────────────────────────────────────────────
const SOCIAL_PLATFORMS = [
  {
    id: "instagram", label: "Instagram", color: "#E1306C",
    Icon: FaInstagram,
    fields: [
      { key: "account_id", label: "Instagram Account ID (opcional)", placeholder: "17841401234567890" },
      { key: "access_token", label: "Token de acceso de Instagram", placeholder: "IGQVJWb...", secret: true },
    ],
    docs: {
      title: "Cómo conectar Instagram",
      steps: [
        "Abrí https://developers.facebook.com → tu App → Panel izquierdo → 'Instagram API with Instagram Login'.",
        "En la sección 'Generar identificadores de acceso', verificá que la cuenta de Instagram del cliente aparezca en la lista.",
        "Si no aparece, hacé clic en 'Añadir cuenta' e iniciá sesión con la cuenta de Instagram del cliente.",
        "Una vez que aparezca la cuenta, hacé clic en 'Generar identificador' — esto genera el Token de acceso.",
        "Copiá ese token y pegalo en el campo 'Token de acceso de Instagram' de arriba.",
        "El ID de la cuenta (debajo del nombre de usuario) es el Instagram Account ID — podés copiarlo también.",
      ],
      link: "https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login",
    },
  },
  {
    id: "facebook", label: "Facebook", color: "#1877F2",
    Icon: FaFacebook,
    fields: [
      { key: "account_id", label: "Page ID", placeholder: "123456789012345" },
      { key: "access_token", label: "Page Access Token", placeholder: "EAABsb...", secret: true },
    ],
    docs: {
      title: "Cómo conectar Facebook",
      steps: [
        "Abrí https://developers.facebook.com y accedé a tu App.",
        "En 'Graph API Explorer', seleccioná tu App.",
        "Hacé clic en 'Get User Access Token', seleccioná los permisos: pages_manage_posts, pages_read_engagement.",
        "Con ese token, hacé GET a /me/accounts para obtener la lista de Páginas y sus tokens.",
        "Copiá el ID de la Página y el access_token de esa Página.",
        "Pegá ambos valores arriba.",
      ],
      link: "https://developers.facebook.com/docs/pages/overview",
    },
  },
  {
    id: "linkedin", label: "LinkedIn", color: "#0A66C2",
    Icon: FaLinkedin,
    fields: [
      { key: "account_id", label: "Organization ID (URN)", placeholder: "urn:li:organization:12345678" },
      { key: "access_token", label: "Access Token", placeholder: "AQV...", secret: true },
    ],
    docs: {
      title: "Cómo conectar LinkedIn",
      steps: [
        "Abrí https://www.linkedin.com/developers/ y creá una App asociada a la Página de empresa.",
        "En la App, agregá los productos 'Marketing Developer Platform' y 'Share on LinkedIn'.",
        "Pedí los scopes: r_organization_social, w_organization_social, r_member_social.",
        "Usá el OAuth 2.0 Authorization Flow para generar el Access Token (token de 60 días).",
        "El Organization ID es el número que aparece en la URL de tu Página de empresa: linkedin.com/company/NÚMERO/",
        "Formatealo como urn:li:organization:NÚMERO y pegalo arriba junto al token.",
      ],
      link: "https://learn.microsoft.com/en-us/linkedin/marketing/getting-started",
    },
  },
  {
    id: "x", label: "X (Twitter)", color: "#000000",
    Icon: FaXTwitter,
    fields: [
      { key: "account_id", label: "Username (sin @)", placeholder: "creativedog_agency" },
      { key: "token_extra.api_key", label: "API Key (Consumer Key)", placeholder: "xvz1evFS...", secret: true, extra: true },
      { key: "token_extra.api_key_secret", label: "API Key Secret", placeholder: "kAcSOqF...", secret: true, extra: true },
      { key: "access_token", label: "Access Token", placeholder: "123456789-abcXYZ...", secret: true },
      { key: "token_extra.access_token_secret", label: "Access Token Secret", placeholder: "LswwdoUaIvS...", secret: true, extra: true },
    ],
    docs: {
      title: "Cómo conectar X / Twitter",
      steps: [
        "Abrí https://developer.twitter.com/en/portal/dashboard y creá un proyecto + App.",
        "Pedí acceso 'Elevated' o 'Basic' según el volumen de publicaciones.",
        "En la App, habilitá 'Read and Write' en los permisos de OAuth 1.0a.",
        "En 'Keys and Tokens', generá el Access Token y Access Token Secret.",
        "Pegá el Access Token y el Access Token Secret en los campos de arriba.",
      ],
      link: "https://developer.twitter.com/en/docs/twitter-api",
    },
  },
  {
    id: "tiktok", label: "TikTok", color: "#000000",
    Icon: FaTiktok,
    fields: [
      { key: "account_id", label: "Open ID (Account ID)", placeholder: "7a1b2c3d4e5f67890" },
      { key: "access_token", label: "Access Token", placeholder: "act.example...", secret: true },
    ],
    docs: {
      title: "Cómo conectar TikTok",
      steps: [
        "Abrí https://developers.tiktok.com y creá una App de tipo 'Business'.",
        "Solicitá acceso a 'Content Posting API' y 'Research API'.",
        "Implementá el flujo OAuth 2.0 (Authorization Code Flow) para obtener el Access Token.",
        "El Open ID se retorna en la respuesta del token como 'open_id'.",
        "Pegá el Open ID y el Access Token arriba.",
      ],
      link: "https://developers.tiktok.com/doc/login-kit-web",
    },
  },
];

const SOCIAL_PLATFORM_MAP = Object.fromEntries(SOCIAL_PLATFORMS.map(p => [p.id, p]));

// ── Social Accounts Card ─────────────────────────────────────────────────────
function SocialAccountsCard({ clientId, canEdit }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null); // platform id
  const [docsFor, setDocsFor] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState({});

  useEffect(() => {
    API.get(`/api/clients/${clientId}/social-accounts`)
      .then(r => setAccounts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const accountMap = Object.fromEntries(accounts.map(a => [a.platform, a]));

  const startConnect = (platform) => {
    const existing = accountMap[platform];
    setForm({
      account_name: existing?.account_name || "",
      account_id: existing?.account_id || "",
      access_token: "",
    });
    setConnecting(platform);
    setShowSecret({});
  };

  const handleSave = async () => {
    if (!form.access_token) { toast("El token de acceso es requerido", "error"); return; }
    setSaving(true);
    const platformCfg = SOCIAL_PLATFORM_MAP[connecting];
    // Build token_extra for fields marked as extra
    const token_extra = {};
    for (const field of platformCfg.fields.filter(f => f.extra)) {
      const keys = field.key.split(".");
      token_extra[keys[keys.length - 1]] = form[field.key] || "";
    }
    try {
      const r = await API.put(`/api/clients/${clientId}/social-accounts/${connecting}`, {
        account_name: form.account_name || null,
        account_id: form.account_id || null,
        access_token: form.access_token,
        token_extra,
      });
      setAccounts(prev => {
        const exists = prev.find(a => a.platform === connecting);
        return exists
          ? prev.map(a => a.platform === connecting ? { ...a, ...r.data } : a)
          : [...prev, r.data];
      });
      setConnecting(null);
      toast("Cuenta conectada", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al conectar", "error");
    } finally { setSaving(false); }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`¿Desconectar ${SOCIAL_PLATFORM_MAP[platform]?.label}?`)) return;
    try {
      await API.delete(`/api/clients/${clientId}/social-accounts/${platform}`);
      setAccounts(prev => prev.filter(a => a.platform !== platform));
      toast("Cuenta desconectada", "success");
    } catch { toast("Error al desconectar", "error"); }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={sectionTitle}>Redes Sociales Conectadas</h3>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
          Conectá las cuentas de redes sociales del cliente para usar el Social Scheduler y publicar desde Smith.
        </p>
        {loading ? <div className="spinner" /> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
            {SOCIAL_PLATFORMS.map(p => {
              const acc = accountMap[p.id];
              const connected = acc?.connected;
              return (
                <div key={p.id} style={{
                  border: `1.5px solid ${connected ? p.color + "50" : "var(--border)"}`,
                  borderRadius: "var(--radius-lg)", padding: "14px 14px",
                  background: connected ? `${p.color}08` : "var(--bg-elevated)",
                  display: "flex", flexDirection: "column", gap: 10,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <p.Icon size={18} color={connected ? p.color : "var(--text-muted)"} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: connected ? "var(--text-primary)" : "var(--text-muted)" }}>
                      {p.label}
                    </span>
                  </div>
                  {connected && acc.account_name && (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.4 }}>@{acc.account_name}</div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 8,
                      background: connected ? "#ECFDF5" : "var(--bg-surface)",
                      color: connected ? "#065F46" : "var(--text-muted)",
                      border: `1px solid ${connected ? "#6EE7B7" : "var(--border)"}`,
                    }}>
                      {connected ? "✓ Conectado" : "No conectado"}
                    </span>
                  </div>
                  {canEdit && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: "3px 9px" }}
                        onClick={() => startConnect(p.id)}>
                        {connected ? "Reconectar" : "Conectar"}
                      </button>
                      {connected && (
                        <button className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, padding: "3px 9px", color: "var(--danger)" }}
                          onClick={() => handleDisconnect(p.id)}>
                          Quitar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Connect modal */}
      {connecting && (
        <Modal onClose={() => setConnecting(null)}>
          {(() => {
            const pCfg = SOCIAL_PLATFORM_MAP[connecting];
            return (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <pCfg.Icon size={22} color={pCfg.color} />
                  <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Conectar {pCfg.label}</h2>
                </div>
                <button
                  style={{ fontSize: 12, color: "var(--accent)", background: "none", border: "none",
                    cursor: "pointer", padding: "0 0 16px", textDecoration: "underline", fontFamily: "inherit" }}
                  onClick={() => { setDocsFor(connecting); }}
                >
                  📖 ¿Cómo obtengo estas credenciales? Ver instrucciones →
                </button>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={modalLabelStyle}>Nombre de la cuenta (opcional)</label>
                    <input className="input" value={form.account_name || ""}
                      onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
                      placeholder="Ej: @creativedog" />
                  </div>
                  {pCfg.fields.map(field => (
                    <div key={field.key}>
                      <label style={modalLabelStyle}>{field.label}</label>
                      <div style={{ position: "relative" }}>
                        <input
                          className="input"
                          type={field.secret && !showSecret[field.key] ? "password" : "text"}
                          value={form[field.key] || ""}
                          onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                          placeholder={field.placeholder}
                          style={{ paddingRight: field.secret ? 36 : undefined }}
                        />
                        {field.secret && (
                          <button
                            type="button"
                            onClick={() => setShowSecret(s => ({ ...s, [field.key]: !s[field.key] }))}
                            style={{
                              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                              background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--text-muted)",
                            }}
                          >
                            {showSecret[field.key] ? "🙈" : "👁"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
                  <button className="btn btn-secondary" onClick={() => setConnecting(null)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Guardando..." : "Conectar cuenta"}
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Docs modal */}
      {docsFor && (() => {
        const pCfg = SOCIAL_PLATFORM_MAP[docsFor];
        return (
          <Modal onClose={() => setDocsFor(null)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <pCfg.Icon size={22} color={pCfg.color} />
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{pCfg.docs.title}</h2>
            </div>
            <ol style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              {pCfg.docs.steps.map((step, i) => (
                <li key={i} style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>{step}</li>
              ))}
            </ol>
            <div style={{ marginTop: 18, padding: "12px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}>
              <a href={pCfg.docs.link} target="_blank" rel="noreferrer"
                style={{ fontSize: 13, color: "var(--accent)", textDecoration: "none", fontWeight: 500 }}>
                📚 Ver documentación oficial →
              </a>
            </div>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => setDocsFor(null)}>Entendido, volver al formulario</button>
            </div>
          </Modal>
        );
      })()}
    </>
  );
}

const modalLabelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: ".06em",
  color: "var(--text-muted)", marginBottom: 6,
};

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{value}</span>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="card" style={{ width: "100%", maxWidth: 480, padding: 32 }}>{children}</div>
    </div>
  );
}

function ContactsCard({ clientId, canEdit, defaultLeaderEmail, defaultPmEmail }) {
  const toast = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: "add" | "edit", data }
  const [form, setForm] = useState({ name: "", email: "", role: "", is_primary: false });
  const [saving, setSaving] = useState(false);
  const [leaderEmail, setLeaderEmail] = useState(defaultLeaderEmail || "rolguin@creativedog.agency");
  const [editingLeader, setEditingLeader] = useState(false);
  const [savingLeader, setSavingLeader] = useState(false);
  const [pmEmail, setPmEmail] = useState(defaultPmEmail || "nahuel@creativedog.agency");
  const [editingPm, setEditingPm] = useState(false);
  const [savingPm, setSavingPm] = useState(false);

  useEffect(() => {
    API.get(`/api/clients/${clientId}/contacts`)
      .then(r => setContacts(r.data))
      .finally(() => setLoading(false));
  }, [clientId]);

  const openAdd = () => {
    setForm({ name: "", email: "", role: "", is_primary: false });
    setModal({ mode: "add" });
  };

  const openEdit = (contact) => {
    setForm({ name: contact.name, email: contact.email, role: contact.role || "", is_primary: contact.is_primary });
    setModal({ mode: "edit", id: contact.id });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast("Nombre y email son requeridos", "error"); return;
    }
    setSaving(true);
    try {
      if (modal.mode === "add") {
        const r = await API.post(`/api/clients/${clientId}/contacts`, form);
        if (form.is_primary) {
          setContacts(prev => prev.map(c => ({ ...c, is_primary: false })).concat(r.data));
        } else {
          setContacts(prev => [...prev, r.data]);
        }
        toast("Contacto agregado", "success");
      } else {
        const r = await API.patch(`/api/clients/${clientId}/contacts/${modal.id}`, form);
        setContacts(prev => {
          const updated = form.is_primary
            ? prev.map(c => ({ ...c, is_primary: c.id === modal.id }))
            : prev.map(c => c.id === modal.id ? r.data : c);
          return updated;
        });
        toast("Contacto actualizado", "success");
      }
      setModal(null);
    } catch (err) {
      toast(err.response?.data?.error || "Error al guardar", "error");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar a "${name}"?`)) return;
    try {
      await API.delete(`/api/clients/${clientId}/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast("Contacto eliminado", "success");
    } catch { toast("Error al eliminar", "error"); }
  };

  const handleSaveLeader = async () => {
    setSavingLeader(true);
    try {
      await API.patch(`/api/clients/${clientId}/contacts/leader-email`, { email: leaderEmail });
      setEditingLeader(false);
      toast("Email del líder actualizado", "success");
    } catch { toast("Error al guardar", "error"); }
    finally { setSavingLeader(false); }
  };

  const handleSavePm = async () => {
    setSavingPm(true);
    try {
      await API.patch(`/api/clients/${clientId}/contacts/pm-email`, { email: pmEmail });
      setEditingPm(false);
      toast("Project Manager actualizado", "success");
    } catch { toast("Error al guardar", "error"); }
    finally { setSavingPm(false); }
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={sectionTitle}>Contactos del cliente</h3>
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Agregar contacto</button>
          )}
        </div>

        {loading ? <div className="spinner" /> : contacts.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>Sin contactos cargados.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Nombre", "Email", "Rol", "Principal", ""].map(h => (
                    <th key={h} style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "var(--text-primary)" }}>{c.name}</td>
                    <td style={{ padding: "10px 12px", color: "var(--text-secondary)" }}>{c.email}</td>
                    <td style={{ padding: "10px 12px", color: "var(--text-muted)" }}>{c.role || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {c.is_primary
                        ? <span style={{ fontSize: 11, background: "rgba(34,197,94,0.15)", color: "var(--success)", padding: "2px 8px", borderRadius: 99 }}>Principal</span>
                        : <span style={{ color: "var(--text-muted)" }}>—</span>
                      }
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {canEdit && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>✏️</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={() => handleDelete(c.id, c.name)}>✕</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Project leader email */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
            Líder de proyecto (CC en emails)
          </div>
          {editingLeader ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="input"
                value={leaderEmail}
                onChange={e => setLeaderEmail(e.target.value)}
                placeholder="rolguin@creativedog.agency"
                style={{ fontSize: 13, maxWidth: 320 }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleSaveLeader} disabled={savingLeader}>
                {savingLeader ? <span className="spinner" style={{ width: 10, height: 10 }} /> : "Guardar"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditingLeader(false); setLeaderEmail(defaultLeaderEmail || "rolguin@creativedog.agency"); }}>
                Cancelar
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{leaderEmail || "rolguin@creativedog.agency"}</span>
              {canEdit && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingLeader(true)}>Editar</button>
              )}
            </div>
          )}
        </div>

        {/* Project manager email */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>
            Project Manager (remitente de emails)
          </div>
          {editingPm ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                className="input"
                value={pmEmail}
                onChange={e => setPmEmail(e.target.value)}
                placeholder="nahuel@creativedog.agency"
                style={{ fontSize: 13, maxWidth: 320 }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleSavePm} disabled={savingPm}>
                {savingPm ? <span className="spinner" style={{ width: 10, height: 10 }} /> : "Guardar"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditingPm(false); setPmEmail(defaultPmEmail || "nahuel@creativedog.agency"); }}>
                Cancelar
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{pmEmail || "nahuel@creativedog.agency"}</span>
              {canEdit && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingPm(true)}>Editar</button>
              )}
            </div>
          )}
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            Este email debe pertenecer a alguien que haya iniciado sesión en Smith con su cuenta Google.
          </p>
        </div>
      </div>

      {modal && (
        <Modal onClose={() => setModal(null)}>
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 20 }}>
            {modal.mode === "add" ? "Agregar contacto" : "Editar contacto"}
          </h2>
          <div className="field">
            <label className="label">Nombre *</label>
            <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Juan Pérez" />
          </div>
          <div className="field">
            <label className="label">Email *</label>
            <input className="input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="juan@empresa.com" />
          </div>
          <div className="field">
            <label className="label">Rol / Cargo</label>
            <input className="input" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="Dueño, Marketing, Técnico..." />
          </div>
          <div className="field">
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={e => setForm(p => ({ ...p, is_primary: e.target.checked }))}
                style={{ accentColor: "var(--accent)", width: 14, height: 14 }}
              />
              Contacto principal
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Guardando...</> : "Guardar"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

const EMAIL_STATUS_LABELS = {
  pending: { label: "Sin respuesta", color: "var(--text-muted)", bg: "rgba(255,255,255,0.06)" },
  reminder_1_sent: { label: "Recordatorio 1 enviado", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  reminder_2_sent: { label: "Recordatorio 2 enviado", color: "#f97316", bg: "rgba(249,115,22,0.12)" },
  responded: { label: "Respondido", color: "var(--success)", bg: "rgba(34,197,94,0.12)" },
  closed: { label: "Cerrado", color: "var(--text-muted)", bg: "rgba(255,255,255,0.06)" },
};

function EmailLogCard({ clientId, canEdit }) {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    API.get(`/api/pm/email-log/${clientId}`)
      .then(r => setLogs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const updateStatus = async (logId, status) => {
    setUpdating(logId);
    try {
      const r = await API.patch(`/api/pm/email-log/${logId}/status`, { status });
      setLogs(prev => prev.map(l => l.id === logId ? r.data : l));
      toast("Estado actualizado", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al actualizar", "error");
    } finally { setUpdating(null); }
  };

  if (!loading && logs.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h3 style={sectionTitle}>Historial de emails del PM</h3>
      {loading ? <div className="spinner" /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["Asunto", "Enviado", "Estado", ""].map(h => (
                  <th key={h} style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const st = EMAIL_STATUS_LABELS[log.status] || EMAIL_STATUS_LABELS.pending;
                const isUpdating = updating === log.id;
                const canRespond = ["pending", "reminder_1_sent", "reminder_2_sent"].includes(log.status);
                const canClose = log.status !== "closed";
                return (
                  <tr key={log.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "var(--accent)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }} onClick={() => setSelectedLog(log)}>
                      {log.subject}
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {new Date(log.sent_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, background: st.bg, color: st.color, padding: "3px 8px", borderRadius: 99, fontWeight: 500, whiteSpace: "nowrap" }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {canEdit && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                          {canRespond && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: "var(--success)", whiteSpace: "nowrap" }}
                              disabled={isUpdating}
                              onClick={() => updateStatus(log.id, "responded")}
                            >
                              {isUpdating ? <span className="spinner" style={{ width: 10, height: 10 }} /> : "Respondido"}
                            </button>
                          )}
                          {canClose && log.status !== "responded" && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}
                              disabled={isUpdating}
                              onClick={() => updateStatus(log.id, "closed")}
                            >
                              {isUpdating ? <span className="spinner" style={{ width: 10, height: 10 }} /> : "Cerrar"}
                            </button>
                          )}
                          {log.status === "responded" && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}
                              disabled={isUpdating}
                              onClick={() => updateStatus(log.id, "closed")}
                            >
                              {isUpdating ? <span className="spinner" style={{ width: 10, height: 10 }} /> : "Cerrar"}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selectedLog && (
        <Modal onClose={() => setSelectedLog(null)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.4 }}>{selectedLog.subject}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedLog(null)} style={{ flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20, fontSize: 12, color: "var(--text-muted)" }}>
            <div><strong style={{ color: "var(--text-secondary)" }}>Para:</strong> {selectedLog.recipients}</div>
            {selectedLog.cc_emails && <div><strong style={{ color: "var(--text-secondary)" }}>CC:</strong> {selectedLog.cc_emails}</div>}
            <div><strong style={{ color: "var(--text-secondary)" }}>Enviado por:</strong> {selectedLog.sent_by_email}</div>
            <div><strong style={{ color: "var(--text-secondary)" }}>Fecha:</strong> {new Date(selectedLog.sent_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
          </div>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <pre style={{ margin: 0, fontFamily: "inherit", fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: 400, overflowY: "auto" }}>
              {selectedLog.body}
            </pre>
          </div>
        </Modal>
      )}
    </div>
  );
}

const ROLE_LABELS = { super_admin: "Super Admin", team_leader: "Team Leader", member: "Miembro" };

function ClientUsersCard({ clientId, canEdit }) {
  const toast = useToast();
  const [allUsers, setAllUsers] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/api/clients/meta/assignable-users"),
      API.get(`/api/clients/${clientId}/users`),
    ]).then(([allRes, assignedRes]) => {
      setAllUsers(allRes.data);
      setAssignedIds(new Set(assignedRes.data.map(u => u.id)));
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (id) => {
    if (!canEdit) return;
    setAssignedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/api/clients/${clientId}/users`, { userIds: [...assignedIds] });
      setDirty(false);
      toast("Equipo actualizado", "success");
    } catch {
      toast("Error al guardar", "error");
    } finally { setSaving(false); }
  };

  // Only show members — team leaders / admins always have access
  const members = allUsers.filter(u => u.role === "member");

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <h3 style={sectionTitle}>Equipo asignado</h3>
        {canEdit && dirty && (
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving
              ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Guardando</>
              : "Guardar"}
          </button>
        )}
      </div>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
        Seleccioná los miembros que podrán trabajar con este cliente. Team Leaders y Admins tienen acceso completo.
      </p>

      {loading ? (
        <div className="spinner" />
      ) : members.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No hay miembros disponibles.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          {members.map(u => {
            const isAssigned = assignedIds.has(u.id);
            return (
              <label
                key={u.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 12px",
                  background: isAssigned ? "rgba(11,0,249,0.06)" : "var(--bg-elevated)",
                  border: `1px solid ${isAssigned ? "var(--accent)" : "var(--border)"}`,
                  borderRadius: "var(--radius-md)",
                  cursor: canEdit ? "pointer" : "default",
                  transition: "background 0.12s, border-color 0.12s",
                }}
              >
                {canEdit && (
                  <input
                    type="checkbox"
                    checked={isAssigned}
                    onChange={() => toggle(u.id)}
                    style={{ accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0 }}
                  />
                )}
                {u.avatar ? (
                  <img src={u.avatar} alt="" style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                    background: "var(--bg-hover)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 600, color: "var(--text-secondary)",
                  }}>
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.email}
                  </div>
                </div>
                {isAssigned && !canEdit && (
                  <span style={{ fontSize: 10, background: "rgba(34,197,94,0.12)", color: "var(--success)", padding: "2px 7px", borderRadius: 99, flexShrink: 0 }}>
                    Asignado
                  </span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditClientModal({ form, setForm, onClose, onSave, isSuperAdmin }) {
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    API.get("/api/settings/industries")
      .then(r => setIndustries(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <Modal onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>Editar cliente</h2>
      <div className="field">
        <label className="label">Nombre</label>
        <input className="input" value={form.name || ""} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
      </div>
      <div className="field">
        <label className="label">Rubro</label>
        <select className="select" value={form.industry || ""} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}>
          <option value="">Seleccioná un rubro</option>
          {industries.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="label">Carpeta de Drive (Documentos)</label>
        <input className="input" value={form.drive_folder_url || ""} onChange={e => setForm(p => ({ ...p, drive_folder_url: e.target.value }))} placeholder="https://drive.google.com/drive/folders/..." />
      </div>
      <div className="field">
        <label className="label">URL del Logo (opcional)</label>
        <input className="input" value={form.logo_url || ""} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." />
      </div>
      <div className="field">
        <label className="label">Color primario</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input type="color" value={form.primary_color || "#0b00f9"} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))}
            style={{ width: 48, height: 40, border: "none", background: "none", cursor: "pointer" }} />
          <input className="input" value={form.primary_color || "#0b00f9"} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} style={{ flex: 1 }} />
        </div>
      </div>
      {isSuperAdmin && (
        <div className="field">
          <label className="label">Estado</label>
          <select className="select" value={form.status || "active"} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>
      )}
      {isSuperAdmin && (
        <div className="field">
          <label className="label">Límite mensual de API (USD)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, color: "var(--text-muted)" }}>$</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Sin límite"
              value={form.monthly_limit_usd ?? ""}
              onChange={e => setForm(p => ({ ...p, monthly_limit_usd: e.target.value === "" ? null : e.target.value }))}
              style={{ flex: 1 }}
            />
          </div>
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
            Dejalo vacío para no aplicar límite. Al alcanzar el límite, los agentes dejan de responder para este cliente.
          </p>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
        <button className="btn btn-primary" onClick={onSave}>Guardar cambios</button>
      </div>
    </Modal>
  );
}
